import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShareModal({ resourceId, resourceType, onClose, onSuccess }) {
  const [users, setUsers] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const handleShare = async () => {
    if (selectedEmails.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = resourceType === 'certificate'
        ? `${API}/certificates/${resourceId}/share`
        : `${API}/credentials/${resourceId}/share`;

      await axios.post(
        endpoint,
        { user_emails: selectedEmails },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${resourceType === 'certificate' ? 'Certificate' : 'Credential'} shared successfully`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (email) => {
    if (selectedEmails.includes(email)) {
      setSelectedEmails(selectedEmails.filter((e) => e !== email));
    } else {
      setSelectedEmails([...selectedEmails, email]);
    }
  };

  const label = resourceType === 'certificate' ? 'Certificate' : 'Credential';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="share-modal">
      <div className="bg-white w-full max-w-md rounded-xl border-2 border-gray-200 shadow-xl">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="font-manrope font-bold text-xl text-text-primary">Share {label}</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="btn-close-share-modal">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <p className="text-text-secondary mb-4 text-sm">Select team members to share with:</p>

          <div className="space-y-2 max-h-64 overflow-y-auto mb-6">
            {users.map((user) => (
              <label
                key={user.id}
                className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-primary transition-colors"
                data-testid={`share-user-option-${user.id}`}
              >
                <input
                  type="checkbox"
                  checked={selectedEmails.includes(user.email)}
                  onChange={() => toggleUser(user.email)}
                  className="w-4 h-4 accent-primary"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary font-medium text-sm truncate">{user.full_name}</p>
                  <p className="text-xs text-text-muted truncate">{user.email}</p>
                </div>
                <span className="text-xs px-2 py-1 bg-gray-100 text-text-muted uppercase rounded-full">
                  {user.role}
                </span>
              </label>
            ))}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleShare}
              disabled={loading || selectedEmails.length === 0}
              className="btn-primary flex-1"
              data-testid="btn-confirm-share"
            >
              {loading ? 'Sharing...' : `Share with ${selectedEmails.length} user${selectedEmails.length !== 1 ? 's' : ''}`}
            </button>
            <button onClick={onClose} className="btn-outline flex-1" data-testid="btn-cancel-share">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
